import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Drives the shared native quantity-selector-component for the product form
 * when a quantity-break option is selected. Uses the component's own public
 * API (setValue / onQuantityChange / updateButtonStates) so Add to cart adds
 * exactly the selected quantity, and everything else that already listens to
 * quantity changes (sticky add-to-cart, etc.) stays in sync.
 *
 * Also listens for ProductSelectEvent (dispatched by the variant picker when
 * the customer switches variant) so the discounted price shown per option
 * always reflects the currently selected variant, not just the variant that
 * was selected when the page first loaded. This mirrors the same pattern
 * `product-price.js` uses for the main price display.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} optionsContainer
 * @extends {Component<Refs>}
 */
export class QuantityBreaksComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles selecting a quantity-break option.
   * @param {Event} event
   */
  selectOption(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const quantity = Number(input.dataset.quantity);
    if (!quantity) return;

    const sectionId = this.dataset.sectionId;
    const productForm = document.querySelector(`product-form-component[data-section-id="${sectionId}"]`);
    const quantitySelector = productForm?.refs?.quantitySelector;

    if (quantitySelector && typeof quantitySelector.setValue === 'function') {
      quantitySelector.setValue(String(quantity));
      quantitySelector.onQuantityChange?.();
      quantitySelector.updateButtonStates?.();
    }
  }

  /**
   * Handles picking a value in one of the per-unit variant selects. Also
   * checks that select's owning quantity-break option, so fiddling with a
   * unit selector always makes its option the active one.
   * @param {Event} event
   */
  selectUnitVariant(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const optionId = select.dataset.optionId;
    if (!optionId) return;

    const radio = /** @type {HTMLInputElement | null} */ (this.querySelector(`#${CSS.escape(optionId)}`));
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Builds the cart items represented by the unit selectors of the currently
   * selected quantity-break option, grouping duplicate variant picks into a
   * single entry with a summed quantity.
   * @returns {Array<{variantId: string, quantity: number}> | null} null when there is
   *   no selected option, or the selected option has no per-unit selectors (product
   *   has a single variant) — callers should fall back to the normal single-variant
   *   add-to-cart flow in that case.
   */
  getBatchItems() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.refs.optionsContainer?.querySelector('input:checked')
    );
    if (!checkedInput) return null;

    const unitSelects = /** @type {NodeListOf<HTMLSelectElement>} */ (
      this.querySelectorAll(`select[data-option-id="${CSS.escape(checkedInput.id)}"]`)
    );
    if (unitSelects.length === 0) return null;

    /** @type {Map<string, number>} */
    const quantitiesByVariant = new Map();

    for (const select of unitSelects) {
      const variantId = select.value;
      if (!variantId) return null;
      quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) || 0) + 1);
    }

    return Array.from(quantitiesByVariant, ([variantId, quantity]) => ({ variantId, quantity }));
  }

  /**
   * Handles the variant picker's ProductSelectEvent: swaps in the freshly
   * server-computed option prices/labels for the newly selected variant,
   * while keeping whichever quantity option the customer had already picked.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newBlock = html.querySelector(`quantity-breaks-component[data-block-id="${this.dataset.blockId}"]`);
        if (!newBlock) return;

        const newOptions = newBlock.querySelector('[ref="optionsContainer"]');
        const currentOptions = this.refs.optionsContainer;
        if (!newOptions || !currentOptions) return;

        const selectedQuantity = currentOptions.querySelector('input:checked')?.dataset.quantity;

        currentOptions.replaceWith(newOptions);

        if (selectedQuantity) {
          const matchingInput = /** @type {HTMLInputElement | null} */ (
            newOptions.querySelector(`input[data-quantity="${selectedQuantity}"]`)
          );
          if (matchingInput) matchingInput.checked = true;
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quantity-breaks] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('quantity-breaks-component')) {
  customElements.define('quantity-breaks-component', QuantityBreaksComponent);
}
